import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

// Safety cap only: avoids unbounded loops when blocks conflict or cart:update chains; tune if needed.
const MAX_AUTO_ADD_ITERATIONS = 50;

/**
 * @typedef {object} CartAutoAddCfg
 * @property {string} blockId
 * @property {number} variantId
 * @property {number} [quantity]
 * @property {string} trackingLogic
 * @property {string} propertyKey
 * @property {boolean} debugDisableOptOut
 * @property {number | null} minSubtotal
 * @property {number | null} maxSubtotal
 * @property {number[]} collectionProductIds
 * @property {number | null} matchProductId
 * @property {string} matchVariantIdsRaw
 * @property {string} sellingPlanId
 * @property {string} lineMessage
 */

/**
 * @typedef {object} CartLine
 * @property {number} variant_id
 * @property {number} quantity
 * @property {number} [final_line_price]
 * @property {number} product_id
 * @property {Record<string, unknown>} [properties]
 * @property {{ selling_plan?: { id: number } }} [selling_plan_allocation]
 */

/**
 * @typedef {object} CartJson
 * @property {number} [items_subtotal_price]
 * @property {number} [item_count]
 * @property {CartLine[]} [items]
 */

const blockState = new WeakMap();
let suppressOptOut = false;
let listenerAttached = false;
let pipelineBusy = false;
let pipelinePending = false;
/** @type {number | undefined} */
let rafId;

function cartSectionsParam() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids].join(',');
}

/**
 * @param {HTMLElement} el
 * @returns {CartAutoAddCfg | null}
 */
function readCfg(el) {
  const s = el.querySelector('script[type="application/json"]');
  if (!s?.textContent?.trim()) return null;
  try {
    return JSON.parse(s.textContent);
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} el
 */
function getState(el) {
  let st = blockState.get(el);
  if (!st) {
    st = { lastEligible: false, lastManagedCount: 0 };
    blockState.set(el, st);
  }
  return st;
}

/**
 * @param {CartAutoAddCfg} cfg
 */
function sellingPlanNum(cfg) {
  const n = cfg.sellingPlanId ? Number.parseInt(cfg.sellingPlanId, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * @param {CartLine} item
 * @param {CartAutoAddCfg} cfg
 * @param {number} plan
 */
function lineMatches(item, cfg, plan) {
  if (item.variant_id !== cfg.variantId) return false;
  if (plan > 0 && item.selling_plan_allocation?.selling_plan?.id !== plan) return false;
  const v = item.properties?.[cfg.propertyKey];
  return v === true || v === 'true';
}

/**
 * @param {CartJson} cart
 * @param {CartAutoAddCfg} cfg
 * @param {number} plan
 */
function managedQty(cart, cfg, plan) {
  let q = 0;
  for (const it of cart.items || []) {
    if (lineMatches(it, cfg, plan)) q += it.quantity;
  }
  return q;
}

/**
 * @param {CartJson} cart
 * @param {CartAutoAddCfg} cfg
 * @param {number} plan
 */
function cartEligible(cart, cfg, plan) {
  const items = cart.items || [];
  let sub = 0;
  const thresholdActive =
    (cfg.minSubtotal != null && cfg.minSubtotal > 0) || (cfg.maxSubtotal != null && cfg.maxSubtotal > 0);

  if (thresholdActive) {
    for (const it of items) {
      if (lineMatches(it, cfg, plan)) continue;
      sub += typeof it.final_line_price === 'number' ? it.final_line_price : 0;
    }
  }

  if (cfg.minSubtotal != null && cfg.minSubtotal > 0 && sub < Math.round(cfg.minSubtotal * 100)) return false;
  if (cfg.maxSubtotal != null && cfg.maxSubtotal > 0 && sub > Math.round(cfg.maxSubtotal * 100)) return false;

  if (cfg.collectionProductIds?.length) {
    const set = new Set(cfg.collectionProductIds.map(Number));
    if (!items.some((i) => set.has(i.product_id))) return false;
  }

  if (cfg.matchProductId != null && cfg.matchProductId > 0) {
    const pid = Number(cfg.matchProductId);
    if (!items.some((i) => i.product_id === pid)) return false;
  }

  const raw = cfg.matchVariantIdsRaw?.trim();
  if (raw) {
    const vs = new Set(
      raw
        .split(',')
        .map((x) => Number.parseInt(x.trim(), 10))
        .filter((n) => Number.isFinite(n))
    );
    if (vs.size > 0 && !items.some((i) => vs.has(i.variant_id))) return false;
  }

  return true;
}

/** @returns {Promise<CartJson>} */
async function loadCart() {
  const res = await fetch(`${Theme.routes.cart_url}.js`);
  return res.json();
}

/**
 * @param {CartJson} cart
 */
function clearOptoutsIfEmpty(cart) {
  const n =
    typeof cart.item_count === 'number' ? cart.item_count : Array.isArray(cart.items) ? cart.items.length : 0;
  if (n !== 0) return;
  for (const node of document.querySelectorAll('cart-auto-add')) {
    if (!(node instanceof HTMLElement)) continue;
    const c = readCfg(node);
    if (c?.blockId) sessionStorage.removeItem(`misen:caa:optout:${c.blockId}`);
  }
}

/**
 * @param {{ sections?: Record<string, string>; errors?: unknown }} parsed
 */
function morphSections(parsed) {
  const sid =
    document.querySelector('cart-items-component[data-section-id]')?.getAttribute('data-section-id') ||
    'cart-auto-add';
  if (!parsed?.sections) return;
  document.dispatchEvent(
    new CartUpdateEvent(parsed, sid, { source: 'cart-auto-add', sections: parsed.sections })
  );
}

/**
 * @param {CartAutoAddCfg} cfg
 * @param {number} plan
 * @param {string} sectionsStr
 */
async function stripManaged(cfg, plan, sectionsStr) {
  suppressOptOut = true;
  try {
    let cur = await loadCart();
    for (let k = 0; k < MAX_AUTO_ADD_ITERATIONS; k++) {
      let line = 0;
      let idx = 0;
      for (const it of cur.items || []) {
        idx++;
        if (lineMatches(it, cfg, plan)) {
          line = idx;
          break;
        }
      }
      if (!line) break;
      const body = JSON.stringify({
        line,
        quantity: 0,
        sections: sectionsStr,
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsed = JSON.parse(await response.text());
      if (parsed.errors) break;
      morphSections(parsed);
      cur = await loadCart();
    }
  } finally {
    suppressOptOut = false;
  }
}

/**
 * @param {CartAutoAddCfg} cfg
 * @param {number} qty
 * @param {number} plan
 * @param {string} sectionsStr
 */
async function pushManaged(cfg, qty, plan, sectionsStr) {
  /** @type {Record<string, string>} */
  const props = { [cfg.propertyKey]: 'true' };
  if (cfg.lineMessage) {
    const m = cfg.lineMessage.slice(0, 500);
    if (m) props._cart_auto_add_note = m;
  }
  if (cfg.trackingLogic === '_track-disabled') props._cart_auto_add_track_disabled = 'true';

  /** @type {{ id: number; quantity: number; properties: Record<string, string>; selling_plan?: number }} */
  const item = { id: cfg.variantId, quantity: qty, properties: props };
  if (plan > 0) item.selling_plan = plan;

  const res = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item], sections: sectionsStr }),
  });
  const result = await res.json();
  if (result.status) return false;

  document.dispatchEvent(
    new CartAddEvent({}, cfg.blockId, {
      source: 'cart-auto-add',
      variantId: String(cfg.variantId),
      itemCount: qty,
      sections: result.sections,
    })
  );
  return true;
}

/**
 * @param {HTMLElement} el
 * @param {CartJson} cart
 * @param {string} sectionsStr
 */
async function runBlock(el, cart, sectionsStr) {
  const cfg = readCfg(el);
  if (!cfg?.variantId) return false;

  const plan = sellingPlanNum(cfg);
  const okCart = cartEligible(cart, cfg, plan);
  const st = getState(el);
  const prevOk = st.lastEligible;
  const prevQty = st.lastManagedCount;
  const q0 = managedQty(cart, cfg, plan);

  const key = `misen:caa:optout:${cfg.blockId}`;
  let opted = !cfg.debugDisableOptOut && cfg.trackingLogic !== '_include' && sessionStorage.getItem(key) === '1';

  if (
    !suppressOptOut &&
    !cfg.debugDisableOptOut &&
    cfg.trackingLogic !== '_include' &&
    okCart &&
    prevOk &&
    prevQty > 0 &&
    q0 === 0
  ) {
    sessionStorage.setItem(key, '1');
    opted = true;
  }

  const want = Math.min(Math.max(Number(cfg.quantity) || 1, 1), 99);
  let mutated = false;
  let work = cart;

  if (cfg.trackingLogic !== '_include' && !okCart && q0 > 0) {
    await stripManaged(cfg, plan, sectionsStr);
    mutated = true;
    work = await loadCart();
  }

  const q1 = managedQty(work, cfg, plan);
  const shouldAdd =
    okCart && q1 < want && (cfg.trackingLogic === '_include' || cfg.debugDisableOptOut || !opted);

  if (shouldAdd) {
    const add = want - q1;
    if (add > 0 && (await pushManaged(cfg, add, plan, sectionsStr))) {
      mutated = true;
      work = await loadCart();
    }
  }

  st.lastEligible = cartEligible(work, cfg, plan);
  st.lastManagedCount = managedQty(work, cfg, plan);
  return mutated;
}

async function runPass() {
  const els = document.querySelectorAll('cart-auto-add');
  if (!els.length) return;

  const sectionsStr = cartSectionsParam();
  let cart = await loadCart();
  clearOptoutsIfEmpty(cart);

  for (const el of els) {
    if (!(el instanceof HTMLElement)) continue;
    if (await runBlock(el, cart, sectionsStr)) cart = await loadCart();
    clearOptoutsIfEmpty(cart);
  }
}

async function runPipeline() {
  if (pipelineBusy) {
    pipelinePending = true;
    return;
  }
  pipelineBusy = true;
  try {
    for (let i = 0; i < MAX_AUTO_ADD_ITERATIONS; i++) {
      await runPass();
      if (!pipelinePending) break;
      pipelinePending = false;
    }
    pipelinePending = false;
  } finally {
    pipelineBusy = false;
  }
}

function schedule() {
  if (rafId != null) cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(() => {
    rafId = undefined;
    void runPipeline();
  });
}

function onCartUpdateForAutoAdd() {
  if (pipelineBusy) {
    pipelinePending = true;
    return;
  }
  schedule();
}

class CartAutoAdd extends HTMLElement {
  connectedCallback() {
    if (!listenerAttached) {
      listenerAttached = true;
      document.addEventListener('cart:update', onCartUpdateForAutoAdd);
    }
    schedule();
  }
}

if (!customElements.get('cart-auto-add')) {
  customElements.define('cart-auto-add', CartAutoAdd);
}
